import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Styles from './kycVerification.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { _initializeKyc } from '../redux/actions';
import Document from './Documents';
import VerificationForm from './VerificationForm';
import VerifyNumber from './VerifyNumber/VerifyNumber';
import SMSCode from './SMSCode/SMSCode';
import AddressProof from './AddressProof';
import { _getUserInfo } from '../../../redux/actions';
import kycPendingIcon from '../../../assets/icons/imported/kyc-warning.svg';
import kycRejectedIcon from '../../../assets/icons/imported/kyc-danger.svg';
import kycVerifyIcon from '../../../assets/icons/imported/kyc-verify.svg';

const RejectedState = () => (
  <div className={Styles.rejectedContainer}>
    <img src={kycRejectedIcon} width={150} alt='KYC rejected icon' className={Styles.statusIcon} />
    <h3>KYC Verification Rejected</h3>
    <div className={Styles.rejectedContent}>
      <p>Unfortunately, your KYC verification was not successful.</p>
      <ul>
        <li>Unclear or incomplete documents submitted</li>
        <li>Information provided did not match requirements</li>
        <li>Documents did not meet verification standards</li>
      </ul>
      <p>Please contact our support team for assistance and guidance on resubmitting your verification documents.</p>
    </div>
  </div>
)

const PendingAdminState = () => (
  <div className={Styles.pendingContainer}>
    <img src={kycPendingIcon} width={150} height={150} alt='KYC pending icon' className={Styles.statusIcon} />
    <h3>KYC Verification Pending</h3>
    <div className={Styles.pendingContent}>
      <p>We've received your verification documents and they are currently under review by our compliance team.</p>
      <ul>
        <li>Documents are being reviewed by our team</li>
        <li>Typical processing time: 1-2 business days</li>
        <li>You'll receive an email once verified</li>
      </ul>
      <p>Thank you for your patience. You can check this page anytime to see your verification status.</p>
    </div>
  </div>
)

const VerifiedState = () => (
  <div className={Styles.verifiedContainer}>
    <img src={kycVerifyIcon} width={150} alt='KYC verified icon' className={Styles.statusIcon} />
    <h3>KYC Verification Completed</h3>
    <div className={Styles.verifiedContent}>
      <p>Congratulations! Your account has been fully verified.</p>
      <ul>
        <li>All documents have been verified successfully</li>
        <li>You now have full access to all platform features</li>
        <li>You can start trading with higher limits</li>
      </ul>
    </div>
  </div>
)

function KYCVerification() {
  const dispatch = useDispatch()
  const location = useLocation()
  const userData = useSelector(state => state.userData.value)

  const hasPhoneLabel = userData?.labels?.some(label => label.key === 'phone')
  const hasDocumentsLabel = userData?.labels?.some(label => label.key === 'document')
  const kycStatus = userData?.labels?.find(label => label.key === 'document')?.value
  // Check for address label in the labels array, not profiles array
  const addressLabelValue = userData?.labels?.find(label => label.key === 'address')?.value
  const hasAddressLabel = addressLabelValue && addressLabelValue !== 'pending'
  const hasRegistrationLabel = userData?.labels?.some(label => label.key === 'registration_details')
  const isPendingAdmin = (userData?.labels?.find(label => label.key === 'document')?.value === 'sdk_completed' &&
    userData?.labels?.find(label => label.key === 'description')?.value !== 'sdk_completed_rejected')
  const isRejected = userData?.labels?.find(label => label.key === 'document')?.value === 'rejected' ||
    (userData?.labels?.find(label => label.key === 'document')?.value === 'sdk_completed' &&
      userData?.labels?.find(label => label.key === 'description')?.value === 'sdk_completed_rejected')
  const isVerified = userData?.labels?.find(label => label.key === 'document')?.value === 'verified'

  const [phoneNumber, setPhoneNumber] = useState('')
  const [step, setStep] = useState(0)
  const [token, setToken] = useState('')
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Check the URL and refresh user data if the required query parameters are present
  useEffect(() => {
    const currentUrl = new URL(window.location.href)
    const urlParams = new URLSearchParams(currentUrl.search)

    // Extract query parameters like accountId, transactionStatus, etc.
    const accountId = urlParams.get('accountId')
    const transactionStatus = urlParams.get('transactionStatus')

    // Check if the URL has accountId and a successful transaction status
    if (accountId && transactionStatus === 'SUCCESS') {
      // console.log("AccountId and Transaction Status matched. Refreshing user data...")
      setIsRefreshing(true)

      // Fetch updated user data
      dispatch(_getUserInfo())
        .then(() => {
          // console.log("User data refreshed successfully")
          // Clear URL parameters to prevent multiple refreshes
          window.history.replaceState({}, document.title, window.location.pathname)
        })
        .catch(error => {
          // console.error("Error refreshing user data:", error)
        })
        .finally(() => {
          setIsRefreshing(false)
        })
    }
  }, [location, dispatch]) // Depend on location to trigger the effect on URL change

  // Fetch user data when component mounts
  useEffect(() => {
    if (!userData) {
      dispatch(_getUserInfo())
    }
  }, [dispatch, userData])

  useEffect(() => {
    if (step === 3) {
      _initializeKyc(
        data => {
          setToken(data.token)
        },
        err => {
          // console.error(err)
        }
      )
    }
  }, [step])

  // Determine the current step based on user data
  useEffect(() => {
    if (!userData) return // Wait for user data to be loaded

    console.log("KYC Debug - Address Label Value:", addressLabelValue)
    console.log("KYC Debug - Has Address Label:", hasAddressLabel)
    console.log("KYC Debug - Has Registration Label:", hasRegistrationLabel)
    console.log("KYC Debug - Has Phone Label:", hasPhoneLabel)
    console.log("KYC Debug - KYC Status:", kycStatus)

    if (isVerified) {
      setStep(8) // Verified state
      console.log("KYC Debug - Setting step to 8 (Verified state)")
    } else if (isRejected) {
      setStep(7) // Rejected state
      console.log("KYC Debug - Setting step to 7 (Rejected state)")
    } else if (isPendingAdmin) {
      setStep(6) // Pending Admin state
      console.log("KYC Debug - Setting step to 6 (Pending Admin state)")
    } else if (hasDocumentsLabel && kycStatus === 'verified') {
      setStep(5)
      console.log("KYC Debug - Setting step to 5 (Document verified)")
    } else if (kycStatus === 'sdk_started') {
      // If SDK has started but not completed, show a message
      setStep(9) // New state for SDK started
      console.log("KYC Debug - Setting step to 9 (SDK started)")
    } else if (kycStatus === 'sdk_completed') {
      // If SDK completed but admin verification pending, show pending state
      setStep(6) // Same as pending admin
      console.log("KYC Debug - Setting step to 6 (SDK completed)")
    } else if (kycStatus === 'kyc_pending') {
      // If KYC is in pending state but requires restart
      if (hasRegistrationLabel) {
        setStep(4) // Move to document verification
        console.log("KYC Debug - Setting step to 4 (Registration details verified, move to document verification)")
      } else if (hasAddressLabel) {
        setStep(3) // Move to registration details
        console.log("KYC Debug - Setting step to 3 (Address verified, move to registration details)")
      } else if (hasPhoneLabel) {
        setStep(2) // Move to address proof
        console.log("KYC Debug - Setting step to 2 (Phone verified, move to address proof)")
      } else {
        setStep(0) // Start from the beginning
        console.log("KYC Debug - Setting step to 0 (Start from the beginning)")
      }
    } else if (kycStatus === 'sdk_pending') {
      // If SDK is in pending state but requires restart
      if (hasRegistrationLabel) {
        setStep(4) // Move to document verification
      } else if (hasAddressLabel) {
        setStep(3) // Move to registration details
      } else if (hasPhoneLabel) {
        setStep(2) // Move to address proof
      } else {
        setStep(0) // Start from the beginning
      }
    } else if (kycStatus === 'sdk_not_started') {
      // If SDK not started, treat it like a fresh start
      console.log("KYC Debug - SDK not started, checking labels...")

      // First check if address is pending - this takes priority
      if (addressLabelValue === 'pending') {
        setStep(2) // Move to address proof
        console.log("KYC Debug - Setting step to 2 (Address is pending, must complete address proof first)")
      } else if (hasRegistrationLabel) {
        setStep(4) // Move to document verification
        console.log("KYC Debug - Setting step to 4 (Registration details verified, move to document verification)")
      } else if (hasAddressLabel) {
        setStep(3) // Move to registration details
        console.log("KYC Debug - Setting step to 3 (Address verified, move to registration details)")
      } else if (hasPhoneLabel) {
        setStep(2) // Move to address proof
        console.log("KYC Debug - Setting step to 2 (Phone verified, move to address proof)")
      } else {
        setStep(0) // Start from the beginning
        console.log("KYC Debug - Setting step to 0 (Start from the beginning)")
      }
    } else if (hasRegistrationLabel) {
      setStep(4)
    } else if (hasAddressLabel) {
      setStep(3)
    } else if (hasPhoneLabel) {
      setStep(2)
    } else {
      setStep(0)
    }

    // setStep(2)
  }, [hasPhoneLabel, hasDocumentsLabel, hasAddressLabel, hasRegistrationLabel, kycStatus, userData, isVerified, isRejected, isPendingAdmin])

  console.log(hasRegistrationLabel, hasAddressLabel, hasPhoneLabel, kycStatus)

  if (isRefreshing) {
    return <div className={Styles.loadingContainer}>Loading updated KYC status...</div>
  }

  // Add a new component for KYC in progress state
  const KycInProgressState = () => (
    <div className={Styles.pendingContainer}>
      <img src={kycPendingIcon} width={150} height={150} alt='KYC in progress icon' className={Styles.statusIcon} />
      <h3>KYC In Progress</h3>
      <div className={Styles.pendingContent}>
        <p>You have already started the KYC process. Please complete the verification flow.</p>
        <ul>
          <li>Your KYC process has been initiated</li>
          <li>Please complete all verification steps</li>
          <li>Do not refresh or navigate away during verification</li>
        </ul>
        <p>If you encountered issues, please wait a few minutes before trying again.</p>
      </div>
    </div>
  )

  return (
    <>
      {step === 0 && <VerifyNumber setStep={setStep} phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber} />}
      {step === 1 && <SMSCode phoneNumber={phoneNumber} setStep={setStep} />}
      {step === 2 && <AddressProof email={userData?.email} setStep={setStep} />}
      {step === 3 && <VerificationForm setStep={setStep} />}
      {step === 4 && <Document token={token} email={userData?.email} />}
      {/* {step === 4 && <h1>REACHED DOCS HERE</h1>} */}
      {step === 6 && <PendingAdminState />}
      {step === 7 && <RejectedState />}
      {step === 8 && <VerifiedState />}
      {step === 9 && <KycInProgressState />}
    </>
  )
}

export default KYCVerification
